import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatModuleType } from 'app/copied/models/ChatModules';
import { usePersistedLocation } from 'app/modules/tailor/hooks/usePersistedLocation';

import { useOfflineModulesAvailable } from './useOfflineModulesAvailable';
import { useLiveChatModuleAvailable } from './useLiveChatModuleAvailable';
import useModule from './useModule';
import useFetchHelpCenter from './fetch/useFetchHelpCenter';
import useInitialLocation from './useInitialLocation';

export default function useModuleRedirect() {
  const { tailorUrl } = useInitialLocation();
  const { data: helpCenter } = useFetchHelpCenter();
  const [persistedLocation] = usePersistedLocation();
  const [redirected, setRedirected] = useState(false);

  const navigate = useNavigate();
  const offlineModulesAvailable = useOfflineModulesAvailable();
  const liveChatAvailable = useLiveChatModuleAvailable();
  const vaModule = useModule(ChatModuleType.VirtualAssistant);

  const prevStatusChangeDate = useRef<string>('');
  const currentStatusChangeDate = helpCenter?.valid_until_date_str || '';
  const isLiveChatOnly = liveChatAvailable && !offlineModulesAvailable;
  const shouldRedirectToOfflineModule = offlineModulesAvailable && !persistedLocation && !tailorUrl;

  useEffect(() => {
    if (!helpCenter || redirected) return;

    const options = { state: { skipAnimation: true } };

    if (vaModule?.va_as_landing_page_enabled) {
      navigate('/virtual-assistant', options);
    } else if (isLiveChatOnly) {
      navigate('/live-chat', options);
    } else if (shouldRedirectToOfflineModule) {
      navigate('/tailor', options);
    }

    setRedirected(true);
  }, [navigate, helpCenter, redirected, isLiveChatOnly, vaModule, shouldRedirectToOfflineModule]);

  // Reset redirected state when new status change date is received
  useEffect(() => {
    if (!prevStatusChangeDate.current) {
      prevStatusChangeDate.current = currentStatusChangeDate;
    }

    if (currentStatusChangeDate !== prevStatusChangeDate.current) {
      prevStatusChangeDate.current = currentStatusChangeDate;
      setRedirected(false);
    }
  }, [currentStatusChangeDate]);
}
